



























































































import { computed, defineComponent, ref, toRefs } from '@nuxtjs/composition-api'
import { formFieldProps } from '~/constants/form'
import { useClassifiedForm } from '~/compositions/form'
import CToggleableContent from '~/components/shared/configurable/form/CToggleableContent.vue'
import { ensure } from '~/utils/typescript'
import CTabs from '~/components/shared/configurable/tab/CTabs.vue'
import CTab from '~/components/shared/configurable/tab/CTab.vue'
import { faCalendar, faCalendarAlt } from '@fortawesome/free-regular-svg-icons'
import FieldError from '~/components/car/classifieds/form/fieldsets/fields/FieldError.vue'

interface MonthWithPrice {
  name: string
  value: string
  enabled: boolean
  price: number | null
}

interface MonthSpec {
  name: string
  value: string
}

enum InputMethod {
  UNIVERSAL = 0,
  PER_MONTH = 1
}

export default defineComponent({
  components: {
    FieldError,
    CToggleableContent,
    CTabs,
    CTab
  },
  props: formFieldProps,
  setup(props: any) {
    const { specs, name } = toRefs(props)

    const universalPrice = ref<number | null>(null)
    const selectedInputMethod = ref<InputMethod>(InputMethod.UNIVERSAL)

    // Forced to use array here instead of map due to vue2 reactivity
    const monthsItems = ref<MonthWithPrice[]>(
      specs.value.months.map((m: MonthSpec) => ({
        name: m.name,
        enabled: true,
        price: null,
        value: m.value
      }))
    )

    function onValue(v: any) {
      if (!v.value || !v.value.months) {
        universalPrice.value = null
        monthsItems.value = specs.value.months.map((m: MonthSpec) => ({
          name: m.name,
          enabled: true,
          price: null,
          value: m.value
        }))
        return
      }
      const { price: fieldValuePrice, months: fieldValueMonths } = v.value
      universalPrice.value = fieldValuePrice

      if (fieldValuePrice) {
        // Sets the selected input method
        const everyMonthPriceIsSameAsUniversal = Object.entries(
          fieldValueMonths
        ).every(
          ([_name, { price }]) =>
            price === 0 || price === null || price === fieldValuePrice
        )
        selectedInputMethod.value = everyMonthPriceIsSameAsUniversal
          ? InputMethod.UNIVERSAL
          : InputMethod.PER_MONTH
      }

      // Sets each month item's price and enabled fields
      for (const monthItem of monthsItems.value) {
        const { value } = monthItem
        const fieldValueMonth = fieldValueMonths[value]
        if (!fieldValueMonth || fieldValueMonth.price === 0) {
          monthItem.price = 0
          monthItem.enabled = false
        } else {
          monthItem.price = fieldValueMonth.price
        }
      }
    }

    const {
      error,
      debouncedValueChanged,
      valueChanged
    } = useClassifiedForm(props, { onValue })

    function onToggleChange(value: string, isEnabled: boolean) {
      const monthItem = getMonthItem(value)!
      monthItem.enabled = isEnabled
      if (isEnabled) {
        monthItem.price =
          (universalInputMethodIsSelected.value && universalPrice.value) || null
      } else {
        monthItem.price = 0
      }
      valueChanged(name.value, {
        price: universalPrice.value,
        months: createBackendCompatibleMonthsStructure()
      })
    }

    function onMonthPriceInput(value: string, text: string) {
      const monthItem = getMonthItem(value)!
      monthItem.price = getNumberOrNullPrice(text)

      debouncedValueChanged(name.value, {
        price: universalPrice.value,
        months: createBackendCompatibleMonthsStructure()
      })
    }
    function onUniversalPriceInput(text: string) {
      const uniPrice = getNumberOrNullPrice(text)
      universalPrice.value = uniPrice
      for (const month of monthsItems.value) {
        if (month.enabled) {
          month.price = uniPrice
        }
      }
      debouncedValueChanged(name.value, {
        price: uniPrice,
        months: createBackendCompatibleMonthsStructure()
      })
    }

    function getMonthItem(value: string): MonthWithPrice {
      return ensure(monthsItems.value.find(m => m.value === value))
    }

    function getNumberOrNullPrice(priceText: string) {
      if (!priceText) {
        return null
      }
      return Number(priceText)
    }

    function createBackendCompatibleMonthsStructure(): Record<string, number> {
      return monthsItems.value.reduce(
        (obj, item) => ({
          ...obj,
          [item.value]: {
            price: item.price
          }
        }),
        {}
      )
    }

    const universalPriceInputDisabled = computed(
      () => selectedInputMethod.value !== InputMethod.UNIVERSAL
    )

    function monthInputIsDisabled(month: MonthWithPrice) {
      return !month.enabled || universalInputMethodIsSelected.value
    }

    function onPerMonthTabClick() {
      universalPrice.value = null
    }

    const universalInputMethodIsSelected = computed(
      () => selectedInputMethod.value === InputMethod.UNIVERSAL
    )

    const displayError = computed(() => {
      if (typeof error.value === 'string') {
        return error.value
      }
      return error && (error.value as any)?.price
    })

    const icons = {
      allMonths: faCalendar,
      perMonth: faCalendarAlt
    }

    return {
      icons,
      error,
      universalPrice,
      onToggleChange,
      onMonthPriceInput,
      onUniversalPriceInput,
      monthsItems,
      selectedInputMethod,
      universalPriceInputDisabled,
      InputMethod,
      monthInputIsDisabled,
      onPerMonthTabClick,
      displayError
    }
  }
})
